<template>
  <div class="dashboard-container">
    <!-- 地图卡片 - 占满全屏 -->
    <el-card class="fullscreen-card" style="padding: 0; overflow: hidden; position: relative; display: flex; flex-direction: column; height: 100%;">
      <!-- 筛选区域 - 左上角，三个独立div同一行 -->
      <div class="filter-wrapper">
        <!-- 省份筛选 - 独立div -->
        <div class="filter-item">
          <el-select
            v-model="selectedProvince"
            placeholder="请选择省份"
            @change="onProvinceChange"
            class="filter-select"
            size="small"
          >
            <el-option label="全国" value="" />
            <el-option
              v-for="p in provinces"
              :key="p"
              :label="p"
              :value="p"
            />
          </el-select>
        </div>

        <!-- 用户筛选 - 独立div（超级管理员和管理员可见） -->
        <div v-if="hasManagePermission" class="filter-item">
          <el-select
            v-model="selectedUserAccount"
            placeholder="请选择用户"
            @change="onUserChange"
            class="filter-select"
            size="small"
            :loading="userOptionsLoading"
            clearable
            @clear="onUserClear"
          >
            <el-option
              v-for="user in userOptions"
              :key="user.userAccount"
              :label="user.userAccount"
              :value="user.userAccount"
            />
          </el-select>
        </div>

        <!-- 清除设备按钮 - 独立div（仅超级管理员） -->
        <div v-if="isSuperAdmin" class="filter-item clear-device-wrapper">
          <el-input
            v-model="clearInput"
            placeholder="批次号/设备ID"
            class="clear-input"
            size="small"
            clearable
            @keyup.enter.native="clearDevice"
          />
          <el-button
            type="danger"
            size="small"
            :loading="clearLoading"
            @click="clearDevice"
          >
            清除设备
          </el-button>
        </div>
      </div>

      <!-- 连接状态指示器和刷新按钮 -->
      <div class="status-wrapper">
        <span class="status-label">实时数据:</span>
        <el-tag :type="wsConnected ? 'success' : 'danger'" size="small">
          {{ wsConnected ? '已连接' : '未连接' }}
        </el-tag>
        <el-divider direction="vertical" />
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh-right"
          :loading="refreshLoading"
          @click="refreshPage"
          class="refresh-btn"
        >
          刷新
        </el-button>
      </div>

      <!-- 饼图卡片 -->
      <div class="floating-pie-card">
        <div class="stats-header">
          <div class="stat-item stat-total">
            <span class="stat-label">总数</span>
            <span class="stat-value">{{ statistics.total }}</span>
          </div>
          <div class="stat-item stat-online">
            <span class="stat-label">在线</span>
            <span class="stat-value">{{ statistics.online }}</span>
          </div>
          <div class="stat-item stat-normal">
            <span class="stat-label">正常</span>
            <span class="stat-value">{{ statistics.normal }}</span>
          </div>
          <div class="stat-item stat-alarm">
            <span class="stat-label">报警</span>
            <span class="stat-value">{{ statistics.alarm }}</span>
          </div>
          <div class="stat-item stat-offline">
            <span class="stat-label">离线</span>
            <span class="stat-value">{{ statistics.offline }}</span>
          </div>
        </div>
        <div ref="pieChart" class="pie-chart-wrapper" />
      </div>

      <!-- 右下角浮动定位按钮 -->
      <div class="location-btn-container" @click="locateToMyPosition">
        <div class="location-btn" :class="{ 'locating': locating }">
          <i class="el-icon-location" :class="{ 'rotating': locating }"></i>
        </div>
      </div>

      <!-- 地图容器 -->
      <div id="map" class="fullscreen-map">
        <!-- 地图加载提示 -->
        <div v-if="mapLoading" class="map-loading-overlay">
          <div class="map-loading-content">
            <div class="map-spinner"></div>
            <div class="map-loading-text">
              地图加载中，请稍后<span class="map-loading-dots"></span>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 设备详情弹窗 -->
    <el-dialog
      :visible.sync="showDevicePopup"
      width="320px"
      :show-close="false"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :modal="false"
      custom-class="device-dialog"
      @close="closePopup"
    >
      <div class="device-dialog-content" :class="getDeviceDialogClass(currentDevice.deviceStatus)">
        <div class="popup-header">
          <img :src="logoUrl" alt="Logo" class="popup-logo"/>
          <span class="popup-title">设备信息</span>
          <span class="popup-close" @click="closePopup">×</span>
        </div>
        <div class="popup-body">
          <div class="info-row" @click="copyText(currentDevice.deviceId || '未知', '设备ID')">
            <span class="label">设备ID：</span>
            <span class="value">{{ currentDevice.deviceId || '未知' }}</span>
          </div>
          <div class="info-row" @click="copyText(currentDevice.batchNo || '未知', '批次号')">
            <span class="label">批次号：</span>
            <span class="value">{{ currentDevice.batchNo || '未知' }}</span>
          </div>
          <div class="info-row" @click="copyText(currentDevice.province || '未知', '省份')">
            <span class="label">省份：</span>
            <span class="value">{{ currentDevice.province || '未知' }}</span>
          </div>
          <div class="info-row" @click="copyText(getStatusText(currentDevice.deviceStatus), '设备状态')">
            <span class="label">设备状态：</span>
            <span class="value" :class="getStatusClass(currentDevice.deviceStatus)">{{ getStatusText(currentDevice.deviceStatus) }}</span>
          </div>
          <div class="info-row" @click="copyText(getOnlineStatusText(currentDevice.onlineStatus), '联机状态')">
            <span class="label">联机状态：</span>
            <span class="value" :class="currentDevice.onlineStatus === '1' ? 'online-status-connected' : 'online-status-single'">
              {{ getOnlineStatusText(currentDevice.onlineStatus) }}
            </span>
          </div>
          <div class="info-row" @click="copyText(getPairStatusText(currentDevice), '配对状态')">
            <span class="label">配对状态：</span>
            <span class="value" :class="getPairStatusClass(currentDevice)">
              {{ getPairStatusText(currentDevice) }}
            </span>
          </div>
          <div class="info-row" @click="copyText(currentDevice.voltageLevel || '未知', '报警电压等级')">
            <span class="label">报警电压等级：</span>
            <span class="value alarm-value">{{ currentDevice.voltageLevel || '未知' }}</span>
          </div>
          <div class="info-row" @click="copyText(formatLocation(currentDevice), '位置')">
            <span class="label">位置：</span>
            <span class="value">{{ formatLocation(currentDevice) }}</span>
          </div>
          <div class="info-row" @click="copyText(formatTime(currentDevice.timestamp), '最后更新时间')">
            <span class="label">最后更新：</span>
            <span class="value">{{ formatTime(currentDevice.timestamp) }}</span>
          </div>
          <div v-if="currentDevice.deviceStatus === '2'" class="offline-tip">
            <span>设备已离线</span>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 聚合弹窗 -->
    <el-dialog
      :visible.sync="showClusterPopup"
      width="350px"
      :show-close="false"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :modal="false"
      custom-class="cluster-dialog"
      @close="closeClusterPopup"
    >
      <div class="cluster-dialog-content">
        <div class="popup-header">
          <img :src="logoUrl" alt="Logo" class="popup-logo"/>
          <span class="popup-title">此区域设备汇总</span>
          <span class="cluster-count">{{ clusterTotal }}台</span>
          <span class="popup-close" @click="closeClusterPopup">×</span>
        </div>
        <div class="popup-body">
          <div class="cluster-stats">
            <span class="stat-normal-badge">正常: {{ clusterStats.normal }}</span>
            <span class="stat-alarm-badge">报警: {{ clusterStats.alarm }}</span>
            <span class="stat-offline-badge">离线: {{ clusterStats.offline }}</span>
          </div>
          <div class="cluster-list">
            <div
              v-for="device in clusterDevices"
              :key="device.deviceId"
              class="cluster-item"
              :class="getClusterItemClass(device.deviceStatus)"
              @click="selectDeviceFromCluster(device)"
            >
              <div class="cluster-item-left">
                <span class="cluster-status-icon" :style="{ backgroundColor: getDotColor(device.deviceStatus) }"></span>
                <span class="cluster-item-id">{{ device.deviceId || '未知' }}</span>
              </div>
              <div class="cluster-item-right">
                <span class="cluster-item-time">{{ formatTime(device.timestamp) }}</span>
              </div>
            </div>
            <div v-if="clusterDevices.length === 0" class="cluster-empty">
              <span>附近暂无设备</span>
            </div>
          </div>
        </div>
        <div class="popup-footer" @click="closeClusterPopup">
          <span>点击设备查看详情</span>
        </div>
      </div>
    </el-dialog>

    <!-- 定位权限引导弹窗 -->
    <el-dialog
      :visible.sync="showLocationGuide"
      width="420px"
      :show-close="true"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      custom-class="location-guide-dialog"
      @close="showLocationGuide = false"
    >
      <div slot="title" class="location-guide-title">
        <i class="el-icon-warning" style="color: #e6a23c; font-size: 20px; margin-right: 8px;"></i>
        开启定位权限
      </div>
      <div class="location-guide-body">
        <p>定位权限已被阻止，请按以下步骤操作：</p>
        <p>1. 点击地址栏左侧的图标</p>
        <p>2. 找到"位置"权限，选择"允许"</p>
        <p>3. 刷新页面后，再次点击定位按钮</p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="showLocationGuide = false">我知道了</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import WebSocketService from '@/components/websocket/webSocketService'
import echarts from 'echarts'
import { mapGetters } from 'vuex'
import { getAllProvinces, getProvinceCenter } from '@/components/gps/provinceUtils'
import { wgs84ToGcj02 } from '@/components/gps/gpsCorrect'
import userPositionIcon from '@/assets/map/user-position.png'
import logoImg from '@/assets/login/max-logo.png'

export default {
  name: 'Welcome',
  data() {
    return {
      // ==================== 缓存相关 ====================
      cacheKey: 'welcomeCache',
      cachedMapCenter: [95.0, 38.0],
      cachedMapZoom: 4,

      // ==================== 地图加载状态 ====================
      mapLoading: true,
      mapInitialized: false,

      // ==================== 省份筛选 ====================
      selectedProvince: '',
      provinces: getAllProvinces(),

      // ==================== 用户筛选 ====================
      selectedUserAccount: '',
      userOptions: [],
      userOptionsLoading: false,
      isUserOptionsLoaded: false,

      // ==================== 设备数据 ====================
      deviceMap: new Map(),
      deviceList: [],
      filteredDevices: [],
      statistics: {
        total: 0,
        online: 0,
        normal: 0,
        alarm: 0,
        offline: 0
      },

      // ==================== 地图相关 ====================
      map: null,
      heatmap: null,
      heatmapVisible: false,
      markerZoomThreshold: 8,
      heatmapZoomThreshold: 8,
      pie: null,
      infoWindow: null,
      massMarks: null,
      currentZoom: 8,
      isShowingMarkers: true,

      // ==================== WS连接状态 ====================
      wsConnected: false,
      refreshLoading: false,
      mapLoaded: false,
      isProcessingHistory: false,

      // ==================== 定时器 ====================
      refreshTimer: null,
      updateMarkersTimer: null,
      zoomTimer: null,

      // ==================== 定位相关 ====================
      locating: false,
      userMarker: null,
      userInfoWindow: null,

      // ==================== 弹窗相关 ====================
      showDevicePopup: false,
      showClusterPopup: false,
      showLocationGuide: false,
      currentDevice: {},
      clusterDevices: [],
      clusterStats: { normal: 0, alarm: 0, offline: 0 },
      clusterTotal: 0,
      currentClusterCenter: null,
      logoUrl: logoImg,

      // ==================== 清除设备相关 ====================
      clearInput: '',
      clearLoading: false
    }
  },
  computed: {
    ...mapGetters('globalUserInfo', [
      'userAccount',
      'userId',
      'token',
      'roleId',
      'hasManagePermission',
      'isSuperAdmin',
      'isNormalAdmin'
    ]),
  },
  watch: {
    deviceMap: {
      deep: true,
      handler() {
        if (this.showDevicePopup && this.currentDevice.deviceId) {
          const updatedDevice = this.deviceMap.get(this.currentDevice.deviceId)
          if (updatedDevice) {
            this.currentDevice = { ...updatedDevice }
          }
        }
      }
    },
    showDevicePopup(val) {
      if (!val) {
        setTimeout(() => {
          this.currentDevice = {}
        }, 300)
      }
    }
  },

  // ==================== 生命周期钩子 ====================
  async created() {
  },

  async mounted() {
    // 1. 恢复缓存
    try {
      await this.restoreCache()
    } catch (error) {
      console.error('恢复缓存失败:', error)
    }

    // 2. 加载用户选项（包含当前用户）
    try {
      await this.loadUserOptions(true)
    } catch (error) {
      console.error('加载用户选项失败:', error)
    }

    // 3. 设置默认用户账号
    if (!this.selectedUserAccount) {
      // 如果缓存中没有用户账号，使用当前登录用户
      this.selectedUserAccount = this.userAccount
    }

    this.loadAMap()
    this.initWebSocket()
    window.addEventListener('resize', this.handleResize)
    window.selectDevice = this.selectDevice
    this.$nextTick(() => {
      this.updatePie()
    })
  },

  beforeDestroy() {
    this.isDestroying = true

    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    if (this.updateMarkersTimer) clearTimeout(this.updateMarkersTimer)
    if (this.zoomTimer) clearTimeout(this.zoomTimer)

    window.removeEventListener('resize', this.handleResize)
    delete window.selectDevice

    if (this.massMarks) {
      try {
        this.massMarks.setMap(null)
      } catch (e) {}
      this.massMarks = null
    }

    if (this.map) {
      try {
        this.map.destroy()
      } catch (e) {}
    }
    if (this.userMarker) {
      try {
        this.userMarker.setMap(null)
      } catch (e) {}
    }
    if (this.userInfoWindow) {
      try {
        this.userInfoWindow.close()
      } catch (e) {}
    }
  },

  methods: {
    // ==================== 缓存方法 ====================
    saveCache() {
      if (!this.map || !this.map.getCenter) return
      try {
        const center = this.map.getCenter()
        this.$store.dispatch('tabs/savePageCache', {
          key: this.cacheKey,
          params: {
            selectedProvince: this.selectedProvince,
            selectedUserAccount: this.selectedUserAccount,
            mapCenter: [center.getLng(), center.getLat()],
            mapZoom: this.map.getZoom()
          }
        })
      } catch (e) {}
    },

    async restoreCache() {
      const { params } = await this.$store.dispatch('tabs/restorePageCache', {
        key: this.cacheKey,
        defaultParams: {
          selectedProvince: '',
          selectedUserAccount: '',
          mapCenter: [95.0, 38.0],
          mapZoom: 4
        }
      })
      this.selectedProvince = params.selectedProvince || ''
      this.selectedUserAccount = params.selectedUserAccount || ''
      this.cachedMapCenter = params.mapCenter || [95.0, 38.0]
      this.cachedMapZoom = params.mapZoom || 4
    },

    // ==================== 用户下拉相关 ====================
    /**
     * 加载用户下拉选项
     * 当前用户排第一
     */
    async loadUserOptions(keepSelected = false) {
      // 普通用户不加载用户列表
      if (!this.hasManagePermission) {
        this.userOptions = []
        this.isUserOptionsLoaded = true
        return
      }
      this.userOptionsLoading = true
      try {
        const { data: result } = await this.$http.get('/user/childAccounts')
        if (result.status === 200) {
          const records = result.data.records || []

          // 保存当前选中的用户
          const currentSelected = this.selectedUserAccount

          this.userOptions = records.map(user => ({
            userId: user.userId,
            userAccount: user.userAccount
          }))

          // 根据 keepSelected 决定选中哪个
          if (keepSelected && currentSelected) {
            // 保留当前选中的用户
            const exists = this.userOptions.some(u => u.userAccount === currentSelected)
            if (exists) {
              this.selectedUserAccount = currentSelected
              this.selectedUserName = currentSelected
            } else if (this.userOptions.length > 0) {
              // 如果之前选中的用户已不存在，选中第一个
              this.selectedUserAccount = this.userOptions[0].userAccount
              this.selectedUserName = this.userOptions[0].userAccount
            }
          } else {
            // 不保留时选中第一个用户（当前用户）
            if (this.userOptions.length > 0) {
              this.selectedUserAccount = this.userOptions[0].userAccount
              this.selectedUserName = this.userOptions[0].userAccount
            }
          }

          this.isUserOptionsLoaded = true
        } else {
          console.error('获取用户列表失败:', result.message)
          this.userOptions = [{ userAccount: this.userAccount }]
          this.isUserOptionsLoaded = true
        }
      } catch (error) {
        console.error('加载用户选项失败:', error)
        this.userOptions = [{ userAccount: this.userAccount }]
        this.isUserOptionsLoaded = true
      } finally {
        this.userOptionsLoading = false
      }
    },

    /**
     * 清除用户筛选 - 回到当前用户
     */
    onUserClear() {
      this.selectedUserAccount = this.userAccount
      if (this.wsService && this.wsService.isConnected) {
        this.wsService.setUserParams(this.userAccount)
      }
      this.saveCache()
      this.refreshPage()
    },

    /**
     * 用户筛选变化处理
     */
    onUserChange() {
      const targetUserAccount = this.selectedUserAccount || this.userAccount
      // WebSocketService 内部通过 setUserParams 发送
      if (this.wsService && this.wsService.isConnected) {
        this.wsService.setUserParams(targetUserAccount)
      }
      this.saveCache()
      this.refreshDataOnly()
    },

    // ==================== 地图相关 ====================
    handleResize() {
      if (this.map && this.map.resize) this.map.resize()
    },

    loadAMap() {
      const self = this
      this.mapLoading = true
      this.mapInitialized = false

      const oldScript = document.querySelector('script[src*="webapi.amap.com/maps"]')
      if (oldScript) {
        oldScript.remove()
      }

      const script = document.createElement('script')
      script.src = 'https://webapi.amap.com/maps?v=2.0&key=8bfc15b8bcad85218c79aa70777760cf&plugin=AMap.HeatMap,AMap.ToolBar,AMap.Scale'

      script.onload = () => {
        self.mapLoading = false

        try {
          const center = self.cachedMapCenter || [95.0, 38.0]
          const zoom = self.cachedMapZoom || 4

          self.map = new AMap.Map('map', {
            center: center,
            zoom: zoom,
            mapStyle: 'amap://styles/885f50411a296a25de9a732cd1624fd0',
            viewMode: '2D',
            features: ['bg', 'road', 'building', 'point']
          })

          self.map.on('complete', () => {
            self.mapInitialized = true
            self.mapLoaded = true
            self.initHeatmap()
            self.currentZoom = self.map.getZoom()
            self.checkAndToggleMarkers()
            self.checkHeatmapVisibility()

            if (self.selectedProvince && self.selectedProvince !== '') {
              self.locateToProvince(self.selectedProvince)
            }
            if (self.deviceList.length > 0) {
              self.refreshAfterDataLoad()
            }
          })

          self.map.addControl(new AMap.ToolBar())
          self.map.addControl(new AMap.Scale())

          self.map.on('zoomend', () => {
            self.currentZoom = self.map.getZoom()
            self.handleZoomChange()
            self.saveCache()
          })

          self.map.on('moveend', () => {
            self.saveCache()
          })

          self.infoWindow = new AMap.InfoWindow({
            offset: new AMap.Pixel(0, -35),
            autoMove: true,
            closeWhenClickMap: true
          })

          self.userInfoWindow = new AMap.InfoWindow({
            offset: new AMap.Pixel(0, -35),
            autoMove: true,
            closeWhenClickMap: true
          })

        } catch (e) {
          console.warn('地图初始化异常:', e.message)
          self.mapLoading = false
          self.$message.error('地图加载失败，请刷新页面重试')
        }
      }

      script.onerror = () => {
        self.mapLoading = false
        self.$message.error('地图加载失败，请刷新页面重试')
      }

      document.head.appendChild(script)
    },

    handleZoomChange() {
      if (this.zoomTimer) clearTimeout(this.zoomTimer)
      this.zoomTimer = setTimeout(() => {
        this.checkAndToggleMarkers()
        this.checkHeatmapVisibility()
        this.updateUserMarkerVisibility()
      }, 150)
    },

    updateUserMarkerVisibility() {
      if (!this.userMarker) return
      const shouldShow = this.currentZoom >= this.markerZoomThreshold
      if (shouldShow) {
        try {
          this.userMarker.setMap(this.map)
        } catch (e) {}
      } else {
        try {
          this.userMarker.setMap(null)
        } catch (e) {}
        if (this.userInfoWindow) {
          try {
            this.userInfoWindow.close()
          } catch (e) {}
        }
      }
    },

    locateToProvince(provinceName) {
      if (!this.map || !this.mapLoaded || !this.map.getCenter) return
      if (!provinceName || provinceName === '' || provinceName === '全国') {
        this.map.setCenter([95.0, 38.0])
        this.map.setZoom(4)
        return
      }
      const center = getProvinceCenter(provinceName)
      if (center) {
        this.map.setCenter(center)
        this.map.setZoom(7)
      }
      this.saveCache()
    },

    locateToMyPosition() {
      if (this.locating) return
      this.locating = true

      if (!navigator.geolocation) {
        this.$message.error('您的浏览器不支持地理定位')
        this.locating = false
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          try {
            const wgsLng = position.coords.longitude
            const wgsLat = position.coords.latitude
            const [gcjLat, gcjLng] = wgs84ToGcj02(wgsLat, wgsLng)

            this.map.setCenter([gcjLng, gcjLat])
            this.map.setZoom(18)

            if (this.userMarker) {
              try { this.userMarker.setMap(null) } catch (e) {}
            }
            if (this.userInfoWindow) {
              try { this.userInfoWindow.close() } catch (e) {}
            }

            const userIcon = new AMap.Icon({
              size: new AMap.Size(35, 35),
              image: userPositionIcon,
              imageSize: new AMap.Size(35, 35),
            })

            this.userMarker = new AMap.Marker({
              position: [gcjLng, gcjLat],
              icon: userIcon,
              offset: new AMap.Pixel(-17.5, -35),
              zIndex: 1000,
              title: '我的位置'
            })

            if (this.currentZoom >= this.markerZoomThreshold) {
              this.userMarker.setMap(this.map)
            } else {
              this.userMarker.setMap(null)
            }

            this.userInfoWindow = new AMap.InfoWindow({
              content: '<div style="padding: 4px 8px; font-size: 12px; background: rgba(0,0,0,0.7); color: #fff; border-radius: 4px;">我的位置</div>',
              offset: new AMap.Pixel(0, -35),
              autoMove: false,
              closeWhenClickMap: true,
              isCustom: true
            })

            this.userMarker.on('click', () => {
              if (this.infoWindow) {
                try { this.infoWindow.close() } catch (e) {}
              }
              this.userInfoWindow.open(this.map, [gcjLng, gcjLat])
              setTimeout(() => {
                if (this.userInfoWindow) {
                  try { this.userInfoWindow.close() } catch (e) {}
                }
              }, 3000)
            })

            this.locating = false
            this.saveCache()
          } catch (e) {
            this.locating = false
          }
        },
        (error) => {
          console.warn('定位失败:', error.code, error.message)
          this.locating = false

          // 所有错误统一显示引导弹窗
          this.showLocationGuide = false
          this.$nextTick(() => {
            this.showLocationGuide = true
          })
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      )
    },

    // ==================== 热力图相关 ====================
    initHeatmap() {
      if (!this.map || !this.mapLoaded) return
      try {
        this.heatmap = new AMap.HeatMap(this.map, {
          radius: 15,
          opacity: [0.2, 0.95],
          gradient: {
            0.0: '#90EE90',
            0.2: '#52C41A',
            0.4: '#FAAD14',
            0.6: '#FF7A45',
            0.8: '#F5222D',
            1.0: '#A8071A'
          },
          visible: false
        })
        this.checkHeatmapVisibility()
      } catch (e) {
        console.warn('热力图初始化失败:', e.message)
      }
    },

    checkHeatmapVisibility() {
      if (!this.heatmap || !this.mapLoaded || !this.map || !this.map.getZoom) return
      try {
        const currentZoom = this.map.getZoom()
        const shouldShow = currentZoom < this.heatmapZoomThreshold
        const wasVisible = this.heatmapVisible

        this.heatmapVisible = shouldShow
        this.heatmap.setOptions({ visible: shouldShow })

        if (shouldShow) {
          this.updateHeatmap()
        } else if (wasVisible && !shouldShow) {
          this.heatmap.setDataSet({ data: [] })
        }
      } catch (e) {
        console.warn('检查热力图可见性失败:', e.message)
      }
    },

    updateHeatmap() {
      if (!this.heatmap || !this.mapLoaded) return

      try {
        if (!this.heatmapVisible) {
          if (this.heatmap.getOptions().visible === false) {
            this.heatmap.setDataSet({ data: [] })
          }
          return
        }

        const heatData = this.filteredDevices
          .filter(d => {
            const lat = d.gps ? d.gps.lat : d.lat
            const lng = d.gps ? d.gps.lng : d.lng
            return lat && lng && d.deviceStatus !== '2'
          })
          .map(d => {
            const lat = d.gps ? d.gps.lat : parseFloat(d.lat)
            const lng = d.gps ? d.gps.lng : parseFloat(d.lng)
            return { lng: lng, lat: lat, count: d.deviceStatus === '1' ? 2 : 1 }
          })

        if (heatData.length === 0) {
          this.heatmap.setDataSet({ data: [] })
          return
        }

        this.heatmap.setDataSet({
          data: heatData,
          max: Math.max(2, Math.ceil(heatData.length / 5))
        })
      } catch (e) {
        console.warn('更新热力图失败:', e.message)
      }
    },

    // ==================== 标记点相关 ====================
    checkAndToggleMarkers() {
      if (!this.map || !this.mapLoaded || !this.map.getZoom) return

      try {
        const currentZoom = this.map.getZoom()
        const shouldShow = currentZoom >= this.markerZoomThreshold

        if (shouldShow !== this.isShowingMarkers) {
          this.isShowingMarkers = shouldShow
          if (shouldShow) {
            if (this.massMarks) {
              try {
                this.massMarks.setMap(this.map)
              } catch (e) {
                this.massMarks = null
                this.initMassMarks()
              }
            }
            this.updateUserMarkerVisibility()
          } else {
            if (this.massMarks) {
              try {
                this.massMarks.setMap(null)
              } catch (e) {}
            }
            if (this.userMarker) {
              try { this.userMarker.setMap(null) } catch (e) {}
            }
            if (this.infoWindow) {
              try { this.infoWindow.close() } catch (e) {}
            }
            if (this.userInfoWindow) {
              try { this.userInfoWindow.close() } catch (e) {}
            }
          }
        }
      } catch (e) {
        console.warn('切换标记显示失败:', e.message)
      }
    },

    initMassMarks() {
      if (!this.map || !this.mapLoaded || !this.mapInitialized) {
        return
      }

      if (this.isDestroying) {
        return
      }

      if (!this.deviceList || this.deviceList.length === 0) {
        return
      }

      const validDevices = this.deviceList.filter(d => d.lat && d.lng)
      if (validDevices.length === 0) {
        return
      }

      if (this.massMarks) {
        try {
          this.massMarks.setMap(null)
        } catch (e) {}
        this.massMarks = null
      }

      this.$nextTick(() => {
        try {
          if (!this.map || !this.mapLoaded || !this.mapInitialized || this.isDestroying) {
            return
          }

          if (!this.map.getCenter) {
            return
          }

          const clusters = this.clusterDevicesByDistance(validDevices, 10)
          const massData = []

          clusters.forEach(cluster => {
            let centerLat = 0, centerLng = 0
            cluster.forEach(device => {
              centerLat += device.lat
              centerLng += device.lng
            })
            centerLat /= cluster.length
            centerLng /= cluster.length

            let styleIndex = 0
            const isCluster = cluster.length > 1
            if (isCluster) {
              styleIndex = 2
            } else {
              const device = cluster[0]
              const status = device.deviceStatus || '0'
              if (status === '1') {
                styleIndex = 4
              } else if (status === '0') {
                styleIndex = 3
              } else if (status === '2') {
                styleIndex = 0
              } else {
                styleIndex = 0
              }
            }
            massData.push({
              lnglat: [centerLng, centerLat],
              style: styleIndex,
              extData: {
                devices: cluster,
                isCluster: isCluster,
                deviceCount: cluster.length
              }
            })
          })

          if (massData.length === 0) {
            return
          }

          const iconStyles = [
            {
              url: require('@/assets/map/off-line.png'),
              anchor: new AMap.Pixel(17.5, 35),
              size: new AMap.Size(35, 35)
            },
            null,
            {
              url: require('@/assets/map/aggregate.png'),
              anchor: new AMap.Pixel(17.5, 35),
              size: new AMap.Size(35, 35)
            },
            {
              url: require('@/assets/map/on-line.png'),
              anchor: new AMap.Pixel(17.5, 35),
              size: new AMap.Size(35, 35)
            },
            {
              url: require('@/assets/map/alarm.png'),
              anchor: new AMap.Pixel(17.5, 35),
              size: new AMap.Size(35, 35)
            }
          ]

          this.massMarks = new AMap.MassMarks(massData, {
            zIndex: 9999,
            style: iconStyles
          })

          this.massMarks.on('click', (e) => {
            try {
              const extData = e.data.extData
              if (extData.isCluster) {
                this.clusterDevices = extData.devices
                this.clusterTotal = extData.devices.length
                const normalCount = extData.devices.filter(d => d.deviceStatus === '0').length
                const alarmCount = extData.devices.filter(d => d.deviceStatus === '1').length
                const offlineCount = extData.devices.filter(d => d.deviceStatus === '2').length
                this.clusterStats = { normal: normalCount, alarm: alarmCount, offline: offlineCount }
                this.showClusterPopup = true
                this.showDevicePopup = false
              } else {
                this.currentDevice = extData.devices[0]
                this.showDevicePopup = true
                this.showClusterPopup = false
              }
            } catch (err) {}
          })

          if (this.isShowingMarkers && this.map && this.map.getCenter) {
            try {
              this.massMarks.setMap(this.map)
            } catch (e) {
              console.warn('设置MassMarks到地图失败:', e.message)
            }
          }
        } catch (e) {
          console.warn('初始化MassMarks失败:', e.message)
          this.massMarks = null
        }
      }, 100)
    },

    clusterDevicesByDistance(devices, distanceThreshold) {
      const clusters = []
      const used = new Set()

      for (let i = 0; i < devices.length; i++) {
        if (used.has(i)) continue
        const cluster = [devices[i]]
        used.add(i)
        for (let j = i + 1; j < devices.length; j++) {
          if (used.has(j)) continue
          const lat1 = devices[i].gps ? devices[i].gps.lat : parseFloat(devices[i].lat)
          const lng1 = devices[i].gps ? devices[i].gps.lng : parseFloat(devices[i].lng)
          const lat2 = devices[j].gps ? devices[j].gps.lat : parseFloat(devices[j].lat)
          const lng2 = devices[j].gps ? devices[j].gps.lng : parseFloat(devices[j].lng)
          const dist = this.calculateDistance(lat1, lng1, lat2, lng2)
          if (dist < distanceThreshold) {
            cluster.push(devices[j])
            used.add(j)
          }
        }
        clusters.push(cluster)
      }
      return clusters
    },

    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 6371000
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLng = (lng2 - lng1) * Math.PI / 180
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },

    scheduleMapUpdate() {
      if (this.updateMarkersTimer) clearTimeout(this.updateMarkersTimer)
      this.updateMarkersTimer = setTimeout(() => {
        if (this.mapLoaded && this.mapInitialized && this.map && this.map.getCenter) {
          this.initMassMarks()
          this.updateHeatmap()
        }
      }, 300)
    },

    // ==================== 设备数据处理 ====================
    processSingleDevice(data) {
      if (!data.deviceId) return

      const gps = this.getDeviceGps(data.deviceId, data.lat, data.lng)

      const formattedDevice = {
        deviceId: data.deviceId,
        batchNo: data.batchNo,
        currentVoltage: data.current_voltage || data.currentVoltage,
        voltageLevel: data.voltage_level || data.voltageLevel,
        deviceStatus: data.device_status || data.deviceStatus,
        onlineStatus: data.online_status || data.onlineStatus,
        pairStatus: data.pair_status || data.pairStatus,
        groupId: data.group_id || data.groupId,
        province: data.province,
        gps: gps,
        lat: gps ? gps.lat : null,
        lng: gps ? gps.lng : null,
        timestamp: data.timestamp || Date.now()
      }
      this.deviceMap.set(formattedDevice.deviceId, formattedDevice)

      if (this.showDevicePopup && this.currentDevice.deviceId === formattedDevice.deviceId) {
        this.currentDevice = { ...formattedDevice }
      }
    },

    getDeviceGps(deviceId, lat, lng) {
      if (lat && lng) {
        const parsedLat = parseFloat(lat)
        const parsedLng = parseFloat(lng)
        if (!isNaN(parsedLat) && !isNaN(parsedLng) && parsedLat !== 0 && parsedLng !== 0) {
          return { lat: parsedLat, lng: parsedLng }
        }
      }
      return null
    },

    calculateStatisticsFromDevices(devices) {
      let total = devices.length
      let normal = 0
      let alarm = 0
      let offline = 0
      let online = 0
      for (let device of devices) {
        const status = device.deviceStatus
        if (status === '0') normal++
        else if (status === '1') alarm++
        else if (status === '2') offline++
        if (device.onlineStatus === '1') online++
      }
      const onlineCount = normal + alarm
      return { total, online: onlineCount, normal, alarm, offline }
    },

    refreshAfterDataLoad() {
      this.deviceList = Array.from(this.deviceMap.values())
      let filteredList = [...this.deviceList]
      if (this.selectedProvince && this.selectedProvince !== '') {
        filteredList = filteredList.filter(d => d.province === this.selectedProvince)
      }
      this.filteredDevices = filteredList
      this.statistics = this.calculateStatisticsFromDevices(filteredList)
      this.updatePie()
      if (this.heatmapVisible) {
        this.updateHeatmap()
      }

      if (this.showDevicePopup && this.currentDevice.deviceId) {
        const updatedDevice = this.deviceMap.get(this.currentDevice.deviceId)
        if (updatedDevice) {
          this.currentDevice = { ...updatedDevice }
        }
      }
    },

    // ==================== WebSocket相关 ====================
    initWebSocket() {
      var self = this
      var token = this.token
      if (!token) {
        console.log('Token 为空，不建立 WebSocket 连接')
        return
      }
      this.wsService = new WebSocketService()
      this.wsConnected = this.wsService.isConnected
      var handler = function(data) {
        self.handleWebSocketMessage(data)
      }
      this.wsService.onMessage(handler)
      this.wsService.onOpen(function() {
        self.wsConnected = true
        console.log('Welcome: WebSocket 已连接')
      })
      this.wsService.onClose(function() {
        self.wsConnected = false
        console.log('Welcome: WebSocket 已断开')
      })
    },

    handleWebSocketMessage(data) {
      try {
        // 用户绑定批次号变更通知
        if (data.type === 'user_bind_change') {
          this.clearAllDeviceData()
          return
        }

        // 设备缓存清除通知
        if (data.type === 'cache_cleared') {
          this.clearAllDeviceData()
          return
        }

        if (data.type === 'connected') return
        if (this.isHistoryData(data) && data.data) {
          for (let i = 0; i < data.data.length; i++) {
            this.processSingleDevice(data.data[i])
          }
          this.$nextTick(() => {
            this.refreshAfterDataLoad()
            this.scheduleMapUpdate()
          })
          return
        }
        if (data.deviceId) {
          this.processSingleDevice(data)
          this.refreshAfterDataLoad()
          this.scheduleMapUpdate()
          return
        }
        if (data.data && data.data.deviceId) {
          this.processSingleDevice(data.data)
          this.refreshAfterDataLoad()
          this.scheduleMapUpdate()
          return
        }
      } catch (e) {
      }
    },

    isHistoryData(data) {
      return data && data.type === 'device_data' && data.dataType === 'all' && Array.isArray(data.data)
    },

    // ==================== 刷新相关 ====================
    /**
     * 刷新页面 - 用于手动点击刷新按钮（重新加载用户选项 + 刷新数据）
     */
    refreshPage() {
      this.loadUserOptions(true)
      this.refreshDataOnly()
    },

    /**
     * 只刷新数据，不重新加载用户选项
     * 用于切换用户、清空用户筛选等场景
     */
    refreshDataOnly() {
      this.refreshLoading = true
      this.deviceMap.clear()
      this.deviceList = []
      this.filteredDevices = []
      this.statistics = { total: 0, online: 0, normal: 0, alarm: 0, offline: 0 }
      this.updatePie()

      if (this.massMarks) {
        try {
          this.massMarks.setMap(null)
        } catch (e) {}
        this.massMarks = null
      }

      if (this.heatmap) {
        try {
          this.heatmap.setOptions({ visible: false })
          this.heatmap.setDataSet({ data: [] })
        } catch (e) {}
      }
      if (this.infoWindow) {
        try { this.infoWindow.close() } catch (e) {}
      }

      setTimeout(() => {
        if (this.map && this.map.getCenter) {
          if (this.selectedProvince && this.selectedProvince !== '') {
            this.locateToProvince(this.selectedProvince)
          } else {
            this.map.setCenter([95.0, 38.0])
            this.map.setZoom(4)
          }
          this.currentZoom = this.map.getZoom()
          this.isShowingMarkers = false
          this.heatmapVisible = false
          const targetUserAccount = this.selectedUserAccount || this.userAccount
          this.refreshAfterDataLoad()
          this.scheduleMapUpdate()
          this.checkHeatmapVisibility()
          this.checkAndToggleMarkers()
        }
        this.refreshLoading = false
        this.saveCache()
      }, 300)
    },

    // ==================== 数据清理方法 ====================
    /**
     * 清空所有设备数据（用户绑定批次号变更通知、设备缓存清除通知）
     */
    clearAllDeviceData(keepMapState = false) {
      this.deviceMap.clear()
      this.deviceList = []
      this.filteredDevices = []
      this.statistics = { total: 0, online: 0, normal: 0, alarm: 0, offline: 0 }
      this.updatePie()
      if (this.heatmap) {
        this.heatmap.setDataSet({ data: [] })
      }
      if (this.massMarks) {
        this.massMarks.setMap(null)
        this.massMarks = null
      }
      this.showClusterPopup = false
      this.showDevicePopup = false
      this.clusterDevices = []
      this.currentDevice = {}
      return
    },

    // ==================== 省份切换 ====================
    onProvinceChange() {
      this.refreshAfterDataLoad()
      if (this.selectedProvince && this.selectedProvince !== '') {
        this.locateToProvince(this.selectedProvince)
      } else {
        this.locateToProvince('全国')
      }
      this.saveCache()
    },

    // ==================== 工具方法 ====================
    formatTime(timestamp) {
      if (!timestamp) return '未知'
      const date = new Date(timestamp)
      return date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0') + ' ' + date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0') + ':' + date.getSeconds().toString().padStart(2, '0')
    },

    getStatusText(deviceStatus) {
      if (!deviceStatus) return '离线'
      switch (deviceStatus) {
        case '0': return '正常'
        case '1': return '报警'
        case '2': return '离线'
        default: return '离线'
      }
    },

    getOnlineStatusText(onlineStatus) {
      if (!onlineStatus) return '单机'
      switch (onlineStatus) {
        case '0': return '单机'
        case '1': return '联机'
        default: return '单机'
      }
    },

    getPairStatusText(row) {
      const onlineStatus = row.onlineStatus
      const pairStatus = row.pairStatus
      if (onlineStatus === '1' && pairStatus === '0') return '已配对'
      if (onlineStatus === '0' && pairStatus === '1') return '配对中'
      return '未配对'
    },

    getPairStatusClass(row) {
      const onlineStatus = row.onlineStatus
      const pairStatus = row.pairStatus
      if (onlineStatus === '1' && pairStatus === '0') return 'pair-status-paired'
      if (onlineStatus === '0' && pairStatus === '1') return 'pair-status-pairing'
      return 'pair-status-unpaired'
    },

    getDotColor(deviceStatus) {
      if (deviceStatus === '0') return '#67C23A'
      if (deviceStatus === '1') return '#F56C6C'
      return '#909399'
    },

    getDeviceDialogClass(deviceStatus) {
      if (deviceStatus === '0') return 'device-dialog-normal'
      if (deviceStatus === '1') return 'device-dialog-alarm'
      if (deviceStatus === '2') return 'device-dialog-offline'
      return ''
    },

    getStatusClass(deviceStatus) {
      if (deviceStatus === '0') return 'status-normal'
      if (deviceStatus === '1') return 'status-alarm'
      return 'status-offline'
    },

    getClusterItemClass(deviceStatus) {
      if (deviceStatus === '0') return 'cluster-item-normal'
      if (deviceStatus === '1') return 'cluster-item-alarm'
      if (deviceStatus === '2') return 'cluster-item-offline'
      return ''
    },

    selectDevice(deviceId) {
      const device = this.deviceMap.get(deviceId)
      if (device && (device.gps || (device.lat && device.lng))) {
        const lng = device.gps ? device.gps.lng : parseFloat(device.lng)
        const lat = device.gps ? device.gps.lat : parseFloat(device.lat)
        this.map.setCenter([lng, lat])
        this.map.setZoom(15)
        this.currentDevice = device
        this.showDevicePopup = true
      }
    },

    selectDeviceFromCluster(device) {
      this.showClusterPopup = false
      this.currentDevice = device
      this.showDevicePopup = true
    },

    copyText(text, fieldName) {
      if (!text || text === '-' || text === '未知') {
        this.$message({ message: '无内容可复制', type: 'warning', duration: 1500 })
        return
      }
      navigator.clipboard.writeText(text)
      this.$message({ message: fieldName + '已复制', type: 'success', duration: 1500 })
    },

    formatLocation(device) {
      let lng = device.gps ? device.gps.lng : device.longitude
      let lat = device.gps ? device.gps.lat : device.latitude

      if (lng !== null && lng !== undefined && lng !== '') {
        lng = parseFloat(lng)
      } else {
        lng = null
      }
      if (lat !== null && lat !== undefined && lat !== '') {
        lat = parseFloat(lat)
      } else {
        lat = null
      }

      if (lng !== null && lat !== null && !isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0) {
        return Number(lng).toFixed(4) + ', ' + Number(lat).toFixed(4)
      }

      return '未知'
    },

    updatePie() {
      const normal = this.statistics.normal
      const alarm = this.statistics.alarm
      const offline = this.statistics.offline
      if (!this.pie && this.$refs.pieChart) this.pie = echarts.init(this.$refs.pieChart)
      if (this.pie) {
        this.pie.setOption({
          tooltip: { trigger: 'item', formatter: '{b}: {d}%' },
          series: [{
            type: 'pie',
            radius: '55%',
            center: ['50%', '45%'],
            data: [
              { name: '正常', value: normal > 0 ? normal : 0 },
              { name: '报警', value: alarm },
              { name: '离线', value: offline }
            ],
            label: { show: true, formatter: '{b}: {d}%', fontSize: 11 },
            labelLine: { length: 8, length2: 6 }
          }],
          color: ['#67C23A', '#F56C6C', '#909399'],
          backgroundColor: 'transparent'
        })
      }
    },

    closePopup() {
      this.showDevicePopup = false
    },

    closeClusterPopup() {
      this.showClusterPopup = false
      this.clusterDevices = []
      this.currentClusterCenter = null
    },

    // ==================== 清除设备方法（仅超级管理员） ====================
    async clearDevice() {
      const keyword = this.clearInput.trim()
      if (!keyword) {
        this.$message.warning('请输入批次号或设备ID')
        return
      }
      try {
        await this.$confirm(
          '确定要清除包含 "' + keyword + '" 的所有设备吗？此操作不可恢复！',
          '危险操作确认',
          {
            confirmButtonText: '确定清除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
      } catch {
        return
      }
      this.clearLoading = true
      try {
        const response = await this.$http.delete('/device/cache/by-keyword', {
          params: { keyword: keyword }
        })
        if (response.data.success === true) {
          var deletedCount = 0
          var deletedIds = []
          var resultData = response.data.data
          if (typeof resultData === 'string') {
            var match = resultData.match(/成功删除\s*(\d+)\s*台设备缓存/)
            if (match) {
              deletedCount = parseInt(match[1]) || 0
            }
            if (deletedCount === 0) {
              this.$message.warning('未找到匹配的设备')
              this.clearInput = ''
              this.clearLoading = false
              return
            }
          } else if (typeof resultData === 'object' && resultData !== null) {
            deletedIds = resultData.deletedIds || []
            deletedCount = resultData.deletedCount || 0
          }

          if (deletedCount > 0) {
            this.refreshPage()
            this.$message.success('成功清除 ' + deletedCount + ' 台设备（包含 "' + keyword + '"）')
            var self = this
            deletedIds.forEach(function(id) {
              self.deviceMap.delete(id)
            })
            this.clearInput = ''
            this.refreshAfterDataLoad()
            this.scheduleMapUpdate()
          } else {
            this.$message.warning('未找到匹配的设备')
            this.clearInput = ''
          }
        } else {
          this.$message.error(response.data.message || '清除失败')
        }
      } catch (error) {
        console.error('清除设备失败:', error)
        var errorMsg = '清除失败，请检查网络'
        if (error.response && error.response.data && error.response.data.message) {
          errorMsg = error.response.data.message
        } else if (error.message) {
          errorMsg = error.message
        }
        this.$message.error(errorMsg)
      } finally {
        this.clearLoading = false
      }
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  width: 100%;
  height: calc(100vh - 48px);
  overflow: hidden;
}

.fullscreen-card {
  height: 100%;
  border-radius: 0;
}

.fullscreen-card >>> .el-card__body {
  height: 100%;
  padding: 0;
  overflow: hidden;
}

.fullscreen-map {
  width: 100%;
  height: 100%;
  background: #b3cffe !important;
  position: relative;
}

/* ==================== 地图加载提示样式 ==================== */
.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f5f7fa;
  z-index: 10;
  transition: opacity 0.5s ease;
}

.map-loading-content {
  text-align: center;
}

.map-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 16px;
  border: 4px solid #e8ecf1;
  border-top: 4px solid #409EFF;
  border-radius: 50%;
  animation: map-spin 0.9s linear infinite;
}

@keyframes map-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.map-loading-text {
  font-size: 15px;
  color: #666;
  letter-spacing: 1px;
  font-family: 'Microsoft YaHei', 'PingFang SC', Arial, sans-serif;
}

.map-loading-dots::after {
  content: '';
  animation: map-dots 1.5s steps(4, end) infinite;
}

@keyframes map-dots {
  0% { content: ''; }
  25% { content: '.'; }
  50% { content: '..'; }
  75% { content: '...'; }
  100% { content: ''; }
}

/* ==================== 筛选区域样式 ==================== */
.filter-wrapper {
  position: absolute;
  top: 10px;
  left: 15px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-item {
  background: white;
  padding: 4px 8px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  height: 36px;
  display: flex;
  align-items: center;
}

.filter-select {
  width: 160px;
}

.clear-device-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.clear-input {
  width: 170px;
}

/* ==================== 状态指示器样式 ==================== */
.status-wrapper {
  position: absolute;
  top: 10px;
  right: 15px;
  z-index: 100;
  background: white;
  padding: 4px 8px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
}

.status-label {
  font-size: 12px;
  color: #666;
}

.refresh-btn {
  padding: 5px 10px;
  margin-left: 5px;
}

/* ==================== 饼图卡片样式 ==================== */
.floating-pie-card {
  position: absolute;
  top: 70px;
  left: 15px;
  z-index: 99;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 12px 12px 8px 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.4);
  transition: all 0.3s ease;
  min-width: 380px;
}

.floating-pie-card:hover {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.stats-header {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 0 8px 10px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.stat-item {
  text-align: center;
  flex: 1;
  min-width: 60px;
}

.stat-total .stat-value {
  color: #409eff;
}

.stat-online .stat-value {
  color: #67C23A;
}

.stat-normal .stat-value {
  color: #67C23A;
}

.stat-alarm .stat-value {
  color: #f56c6c;
}

.stat-offline .stat-value {
  color: #909399;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #8c8d8e;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
}

.stat-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.pie-chart-wrapper {
  width: 240px;
  height: 210px;
  margin: 0 auto;
}

.location-btn-container {
  position: absolute;
  bottom: 120px;
  right: 15px;
  z-index: 100;
  cursor: pointer;
}

.location-btn {
  width: 44px;
  height: 44px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #409eff;
  transition: all 0.3s ease;
  cursor: pointer;
}

.location-btn:hover {
  background: #f0f0f0;
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
}

.location-btn.locating {
  background: #409eff;
  color: white;
}

.location-btn i {
  transition: transform 0.3s ease;
}

.location-btn i.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ==================== 弹窗统一样式 ==================== */

::v-deep .device-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  background: transparent !important;
  box-shadow: none !important;
}

::v-deep .device-dialog .el-dialog__header,
::v-deep .device-dialog .el-dialog__footer {
  display: none !important;
}

::v-deep .device-dialog .el-dialog__body {
  padding: 0 !important;
  background: transparent !important;
}

::v-deep .cluster-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  background: transparent !important;
  box-shadow: none !important;
}

::v-deep .cluster-dialog .el-dialog__header,
::v-deep .cluster-dialog .el-dialog__footer {
  display: none !important;
}

::v-deep .cluster-dialog .el-dialog__body {
  padding: 0 !important;
  background: transparent !important;
}

.device-dialog-content {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.device-dialog-content.device-dialog-normal {
  background: #67C23A;
}

.device-dialog-content.device-dialog-alarm {
  background: #e36464;
}

.device-dialog-content.device-dialog-offline {
  background: #e0e0e0;
}

.device-dialog-content .popup-header {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  background: inherit;
}

.device-dialog-content.device-dialog-normal .popup-header {
  background: #67C23A;
}

.device-dialog-content.device-dialog-alarm .popup-header {
  background: #e36464;
}

.device-dialog-content.device-dialog-offline .popup-header {
  background: #e0e0e0;
}

.device-dialog-content .popup-logo {
  width: 50px;
  height: 30px;
  margin-right: 8px;
  transform: scale(1.5);
}

.device-dialog-content .popup-title {
  flex: 1;
  font-size: 16px;
  font-weight: bold;
  color: #ffffff;
}

.device-dialog-content .popup-close {
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  color: #ffffff;
  padding: 0 5px;
}

.device-dialog-content .popup-close:hover {
  opacity: 0.7;
}

.device-dialog-content .popup-body {
  padding: 15px;
  background: #ffffff;
}

.device-dialog-content .info-row {
  display: flex;
  margin-bottom: 10px;
  font-size: 13px;
  cursor: pointer;
}

.device-dialog-content .info-row .label {
  width: 95px;
  color: #666;
}

.device-dialog-content .info-row .value {
  flex: 1;
  color: #333;
  word-break: break-all;
}

.device-dialog-content .info-row .value.status-normal {
  color: #67C23A;
  font-weight: 500;
}

.device-dialog-content .info-row .value.status-alarm {
  color: #F56C6C;
  font-weight: 500;
}

.device-dialog-content .info-row .value.status-offline {
  color: #909399;
}

.device-dialog-content .info-row .alarm-value {
  color: #F56C6C;
  font-weight: 500;
}

.device-dialog-content .offline-tip {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
  color: #909399;
  font-size: 12px;
  text-align: center;
}

.cluster-dialog-content {
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.cluster-dialog-content .popup-header {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  background: #409eff !important;
  color: #fff;
}

.cluster-dialog-content .popup-logo {
  width: 50px;
  height: 30px;
  margin-right: 8px;
  transform: scale(1.5);
}

.cluster-dialog-content .popup-title {
  flex: 1;
  font-size: 15px;
  font-weight: bold;
  color: #ffffff;
}

.cluster-dialog-content .cluster-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  margin-right: 8px;
}

.cluster-dialog-content .popup-close {
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  padding: 0 5px;
  color: #ffffff;
}

.cluster-dialog-content .popup-body {
  background: #fff;
}

.cluster-dialog-content .cluster-stats {
  display: flex;
  justify-content: space-around;
  padding: 12px 15px;
  background: #f5f7fa;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.cluster-stats span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.stat-normal-badge::before {
  content: "";
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #67C23A;
  margin-right: 4px;
}

.stat-alarm-badge::before {
  content: "";
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #F56C6C;
  margin-right: 4px;
}

.stat-offline-badge::before {
  content: "";
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #909399;
  margin-right: 4px;
}

.cluster-dialog-content .cluster-list {
  max-height: 300px;
  overflow-y: auto;
}

.cluster-dialog-content .cluster-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}

.cluster-dialog-content .cluster-item:hover {
  background-color: #f5f5f5;
}

.cluster-dialog-content .cluster-item.cluster-item-alarm {
  background-color: rgba(245, 108, 108, 0.08);
}

.cluster-dialog-content .cluster-item.cluster-item-offline {
  background-color: rgba(144, 147, 153, 0.08);
}

.cluster-dialog-content .cluster-item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.cluster-dialog-content .cluster-status-icon {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cluster-dialog-content .cluster-item-id {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cluster-dialog-content .cluster-item-right {
  flex-shrink: 0;
  width: 155px;
  text-align: right;
}

.cluster-dialog-content .cluster-item-time {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
}

.cluster-dialog-content .cluster-empty {
  padding: 30px;
  text-align: center;
  color: #999;
}

.cluster-dialog-content .popup-footer {
  padding: 12px;
  text-align: center;
  font-size: 11px;
  color: #999;
  border-top: 1px solid #eee;
  background: #fafafa;
  cursor: pointer;
}

.cluster-dialog-content .popup-footer:hover {
  background: #f0f0f0;
}

.online-status-connected {
  color: #67C23A !important;
  font-weight: 500;
}

.online-status-single {
  color: #909399 !important;
}

.pair-status-paired {
  color: #67C23A !important;
  font-weight: 500;
}

.pair-status-pairing {
  color: #E6A23C !important;
  font-weight: 500;
}

.pair-status-unpaired {
  color: #909399 !important;
}

/* ==================== 定位引导弹窗样式 ==================== */
.location-guide-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
}

.location-guide-body {
  padding: 8px 0;
  font-size: 14px;
  line-height: 2.2;
}

.location-guide-body p {
  margin: 4px 0;
}

::v-deep .location-guide-dialog .el-dialog__header {
  padding: 16px 20px 8px 20px;
}

::v-deep .location-guide-dialog .el-dialog__body {
  padding: 8px 20px 16px 20px;
}

::v-deep .location-guide-dialog .el-dialog__footer {
  padding: 0 20px 16px 20px;
}
</style>
